import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  FolioInvoiceApiModel,
  FolioInvoiceLineApiModel,
  FolioPaymentLineApiModel,
  ProformaInvoiceApiModel,
  ReservationApiModel,
} from '@rocksoft/inroom-sdk';
import {
  FormInvoice,
  FormInvoiceLine,
  FormPaymentLine,
  MobileFolioFormInvoiceComponent,
} from './mobilefolioforminvoice.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'era-mobilefolio-ui',
  templateUrl: './mobilefolio.component.html',
  imports: [ReactiveFormsModule, FaIconComponent, MobileFolioFormInvoiceComponent],
})
export class MobileFolioComponent {
  private readonly fb = inject(UntypedFormBuilder);

  readonly mobileFolio = input.required<ProformaInvoiceApiModel>();
  readonly reservation = input.required<ReservationApiModel>();

  readonly save = output<ProformaInvoiceApiModel>();

  editMode = false;

  public readonly mobileFolioForm: UntypedFormGroup = this.fb.group({
    invoice: this.fb.array([]),
    proforma: this.fb.array([]),
  });

  get invoice(): UntypedFormArray {
    return this.mobileFolioForm.get('invoice') as UntypedFormArray;
  }

  get proforma(): UntypedFormArray {
    return this.mobileFolioForm.get('proforma') as UntypedFormArray;
  }

  public toggleEditMode() {
    if (this.editMode) {
      this.saveChanges();
    }

    this.editMode = !this.editMode;
  }

  private saveChanges() {
    const result: ProformaInvoiceApiModel = {
      Invoice: (this.invoice.value as FormInvoice[]).map((x) => this.mapInvoiceFromForm(x)),
      Proforma: (this.proforma.value as FormInvoice[]).map((x) => this.mapInvoiceFromForm(x)),
    };

    this.save.emit(result);
  }

  private mapInvoiceLineFromForm(line: FormInvoiceLine): FolioInvoiceLineApiModel {
    return {
      InvoiceLineCode: line.invoiceLineCode,
      Description: line.description,
      Quantity: line.quantity,
      ArticleGroupCode: line.articleGroupCode,
      BillingGroupCode: line.billingGroupCode,
      Total: line.total,
      VatTotal: line.vatTotal,
      PostingDate: line.postingDate,
      CustomReference: line.customReference,
      IsDeleted: line.isDeleted,
      CurrencyCode: line.currencyCode,
      RevenueType: line.revenueType,
    };
  }

  private mapPaymentLineFromForm(line: FormPaymentLine): FolioPaymentLineApiModel {
    return {
      InvoiceLineCode: line.invoiceLineCode,
      Description: line.description,
      Quantity: line.quantity,
      ArticleGroupCode: line.articleGroupCode,
      BillingGroupCode: line.billingGroupCode,
      Total: line.total,
      VatTotal: line.vatTotal,
      PostingDate: line.postingDate,
      CustomReference: line.customReference,
      CurrencyCode: line.currencyCode,
    };
  }

  private mapInvoiceFromForm(invoice: FormInvoice): FolioInvoiceApiModel {
    return {
      ReservationId: +invoice.reservationId,
      Total: invoice.total,
      VatTotal: invoice.vatTotal,
      InvoiceReference: invoice.invoiceReference,
      Lines: invoice.lines.map((x) => this.mapInvoiceLineFromForm(x)),
      Payments: invoice.payments.map((x) => this.mapPaymentLineFromForm(x)),
    };
  }
}
